import React, { useEffect, useRef } from "react";
import styles from "./ScrollCue.module.css";

function getTarget(button, targetId) {
  return targetId
    ? document.getElementById(targetId)
    : button?.closest("[data-scroll-hero]")?.nextElementSibling;
}

function animateScroll(target) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    target.scrollIntoView({ behavior: "instant", block: "start" });
    return () => {};
  }

  const start = window.scrollY;
  const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const end = Math.min(maxScroll, Math.max(0, start + target.getBoundingClientRect().top - padding - margin));
  const startedAt = performance.now();
  const duration = 1100;
  const events = ["wheel", "touchstart", "pointerdown", "keydown", "hashchange"];
  let frame;

  function stop() {
    window.cancelAnimationFrame(frame);
    events.forEach(event => window.removeEventListener(event, stop));
  }
  function step(now) {
    const progress = Math.min(1, (now - startedAt) / duration);
    const eased = progress * progress * (3 - 2 * progress);
    // Bypass CSS smooth scrolling so each frame follows the chosen duration.
    window.scrollTo({ top: start + (end - start) * eased, behavior: "instant" });
    if (progress < 1) frame = window.requestAnimationFrame(step);
    else stop();
  }
  events.forEach(event => window.addEventListener(event, stop, { passive: true }));
  frame = window.requestAnimationFrame(step);
  return stop;
}

export default function ScrollCue({ targetId, label = "Scroll to explore", autoScroll = true }) {
  const buttonRef = useRef(null);
  const stopScrollRef = useRef(() => {});

  useEffect(() => () => stopScrollRef.current(), [targetId]);

  useEffect(() => {
    if (!autoScroll) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const initialHash = window.location.hash;
    // Lazy-loaded pages may mount after the browser tried to find an anchor.
    // Resolve that destination here instead of skipping anchored service links.
    let anchorId;
    try {
      anchorId = decodeURIComponent(initialHash.slice(1));
    } catch {
      return;
    }
    // Preserve restored positions and avoid unsolicited motion.
    if (window.scrollY > 0 || (reducedMotion.matches && !anchorId)) return;

    let timer;
    let cancelled = false;
    const events = ["wheel", "touchstart", "pointerdown", "keydown", "scroll", "hashchange"];
    function cancel() {
      cancelled = true;
      window.clearTimeout(timer);
    }
    function schedule() {
      if (cancelled) return;
      timer = window.setTimeout(() => {
        if (cancelled || window.location.hash !== initialHash || window.scrollY > 0) return;
        if (reducedMotion.matches && !anchorId) return;
        const target = anchorId
          ? document.getElementById(anchorId)
          : getTarget(buttonRef.current, targetId);
        // Automatic scrolling leaves focus with the visitor's current control.
        if (target) {
          stopScrollRef.current();
          stopScrollRef.current = animateScroll(target);
        }
      }, 1800);
    }

    events.forEach(event => window.addEventListener(event, cancel, { passive: true }));
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      cancel();
      window.removeEventListener("load", schedule);
      events.forEach(event => window.removeEventListener(event, cancel));
    };
  }, [targetId, autoScroll]);

  function scrollToContent(event) {
    const target = getTarget(event.currentTarget, targetId);
    if (!target) return;

    // Move keyboard navigation to the content without interrupting the scroll.
    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
      target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
    }
    target.focus({ preventScroll: true });
    stopScrollRef.current();
    stopScrollRef.current = animateScroll(target);
  }

  return (
    <button ref={buttonRef} type="button" className={styles.cue} onClick={scrollToContent}>
      <span className={styles.icon} aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 4v15m-6-6 6 6 6-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{label}</span>
    </button>
  );
}
